import { Action, handleActions } from 'redux-actions'
import { all, call, put, takeLatest, takeLeading } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import * as Bridge from '@/api/BridgeApi'
import {
  fetchScheduledTransfer,
  ICancelScheduledTransferRequest,
  SCHEDULE_PAYMENT_API_URL,
} from '@/api/SchedulePaymentApi'
import { ENTRY_ROUTE_QUERY, SCHEDULE_PAYMENT_ROUTES, STATUS_CODE } from '@/domains/CommonDomain'
import { IScheduledTransferDetail } from '@/domains/SchedulePaymentDomain'
import { EmptyScheduledTransferDetail } from '@/domains/SchedulePaymentDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { loadingActions } from '@/modules/common/loading'
import { createFetchAction, createPostAction, createSaga } from '@/utils/actionUtils'
import { IRequestAuthResult } from '@/utils/bridgeUtils/features/requestAuth'
import { HostType } from '@/utils/bridgeUtils/features/requestPinAuth'
import { newError } from '@/utils/errorUtils'
import { redirect } from '@/utils/historyUtils'
import { $ } from '@/utils/i18nUtils'
import { apiFailureReducer } from '@/utils/reducerUtils'

import { schedulePaymentHistoryActions } from './SchedulePaymentHistoryModule'
import { schedulePaymentActions } from './SchedulePaymentModule'

export const SCHEDULE_PAYMENT_DETAIL = 'SchedulePaymentDetail'
export const CANCEL_SCHEDULE_PAYMENT = `${SCHEDULE_PAYMENT_DETAIL}/cancel`

interface ISchedulePaymentDetailState {
  scheduledTransfer: IScheduledTransferDetail
}

const initialState: ISchedulePaymentDetailState = {
  scheduledTransfer: EmptyScheduledTransferDetail,
}

export const schedulePaymentDetailAsync = createFetchAction<IScheduledTransferDetail>(
  SCHEDULE_PAYMENT_DETAIL,
)
export const schedulePaymentCancelAsync = createPostAction(CANCEL_SCHEDULE_PAYMENT)

export const reducer = {
  [schedulePaymentDetailAsync.SUCCESS]: (
    state: ISchedulePaymentDetailState,
    action: Action<any>,
  ) => {
    return {
      ...state,
      scheduledTransfer: action.payload,
    }
  },
  [schedulePaymentDetailAsync.FAILURE]: apiFailureReducer,
}

export const schedulePaymentDetailSelector = {
  scheduledTransfer: (state: IRootState) =>
    oc(state[SCHEDULE_PAYMENT_DETAIL]).scheduledTransfer(initialState.scheduledTransfer),
}

export function* cancel(action: Action<ICancelScheduledTransferRequest>) {
  try {
    yield put(loadingActions.start(SCHEDULE_PAYMENT_DETAIL))

    const { result }: IRequestAuthResult = yield call(Bridge.requestAuth, {
      hostType: HostType.BANK_API,
      apiUrl: SCHEDULE_PAYMENT_API_URL.CANCEL,
      request: action.payload,
    })

    if (!result) {
      throw newError(STATUS_CODE.TRANSFER_CLIENT_ERROR)
    }

    if (!result.isSuccess) {
      const { code } = JSON.parse(result.response)
      throw newError(code)
    }

    yield all([
      put(schedulePaymentActions.resetState()),
      put(schedulePaymentHistoryActions.resetState()),
    ])
    yield call(redirect, `${SCHEDULE_PAYMENT_ROUTES.ROOT}?${ENTRY_ROUTE_QUERY}`)
    yield call(Bridge.showNotification, { message: $('schedulepayment_closed_toast_desc') })
  } catch (e) {
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(SCHEDULE_PAYMENT_DETAIL))
  }
}

export const schedulePaymentDetailReducer = handleActions(reducer, initialState)
export const schedulePaymentDetailSaga = [
  takeLatest(
    schedulePaymentDetailAsync.FETCH,
    createSaga<IScheduledTransferDetail>(schedulePaymentDetailAsync, fetchScheduledTransfer),
  ),
  takeLeading(schedulePaymentCancelAsync.POST, cancel),
]
